<template>
  <v-menu v-if="agent" v-model="visible" :close-on-content-click="false" content-class="shell-pop" max-width="300" min-width="260"
          nudge-bottom="8" offset-y transition="slide-y-transition" z-index="10">
    <template v-slot:activator="{on: menu, attrs}">
      <v-tooltip bottom>
        <template v-slot:activator="{on: tooltip}">
          <button :aria-label="`Call-Center-Status: ${statusInfo.text}`" :class="[tone, {'shell-pill--pulse': isPaused || !!pendingStatus}]"
                  class="shell-pill" type="button" v-bind="attrs" v-on="{...menu, ...tooltip}">
            <span class="shell-pill__dot"/>
            <span v-if="$vuetify.breakpoint.smAndUp">{{ statusInfo.text }}</span>
          </button>
        </template>
        <span v-if="isPaused">Call-Center: Pause – du bekommst keine Anrufe</span>
        <span v-else>Call-Center (Placetel): {{ statusInfo.text }}</span>
      </v-tooltip>
    </template>
    <v-card>
      <v-list class="pb-0" dense>
        <v-list-item>
          <v-list-item-icon>
            <v-icon :color="statusInfo.color" dense>{{ statusInfo.icon }}</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ agent.name }}</v-list-item-title>
            <v-list-item-subtitle>{{ agent.sipId }}</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        <v-list-item>
          <v-list-item-icon>
            <v-icon dense>mdi-information-outline</v-icon>
          </v-list-item-icon>
          <v-list-item-content>
            <v-list-item-title>{{ statusInfo.text }}</v-list-item-title>
            <v-list-item-subtitle>Aktueller Status</v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
      </v-list>
      <v-alert v-if="isImpersonating" class="mx-2 mb-0 mt-2" dense text type="warning">
        Du änderst den Status des Agents von {{ agent.name }}.
      </v-alert>
      <v-card-actions>
        <v-btn v-for="status in settableStatuses" :key="status"
               :color="agent.status === status ? statusMeta[status].color : undefined"
               :depressed="agent.status === status" :disabled="!!pendingStatus && pendingStatus !== status"
               :loading="pendingStatus === status" :outlined="agent.status !== status" class="flex-grow-1" small
               @click="setStatus(status)">
          <v-icon left small>{{ statusMeta[status].icon }}</v-icon>
          {{ statusMeta[status].text }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script lang="ts">
import Vue from 'vue';
import {isAxiosError} from 'axios';
import {Types} from 'mongoose';
import {ApiHandlerApi} from '@/classes/api/api-handler.api';
import {UserLoginHandlerApi} from '@/classes/api/user-login-handler.api';
import {
  CallCenterAgentPlacetelResponseDto,
} from '@/classes/dto/api-handler/response/placetel/api/CallCenterAgent.placetel.response.dto';
import {
  USER_SETTABLE_CALL_CENTER_AGENT_STATUSES,
  UserSettableCallCenterAgentStatus,
} from '@/classes/dto/api-handler/request/placetel/api/SetCallCenterAgentStatus.placetel.request.dto';
import {CrudEntityTypes} from '@/classes/clientOnly/permissionTreeResources/enums/CrudEntityTypes';
import {LoginUserFields} from '@/classes/clientOnly/permissionTreeResources/enums/entityFields/LoginUserFields';
import {
  CALL_CENTER_AGENT_STATUS_META,
  CallCenterAgentStatusInfo,
  getCallCenterAgentStatusInfo,
  getCallCenterAgentStatusTone,
} from '@/helpers/placetel/callCenterAgentStatus.helper';

/** Placetel lädt bei jedem GET die komplette Agent-Liste über eine rate-limitierte Queue, daher nicht öfter */
const MIN_REFRESH_INTERVAL_MS = 30 * 1000;
const POLLING_INTERVAL_MS = 60 * 1000;

export default Vue.extend({
  data: () => ({
    visible: false,
    agent: null as CallCenterAgentPlacetelResponseDto | null,
    pendingStatus: null as UserSettableCallCenterAgentStatus | null,
    lastLoadedAt: 0,
    pollingInterval: undefined as number | undefined,
  }),
  computed: {
    statusMeta: () => CALL_CENTER_AGENT_STATUS_META,
    settableStatuses: () => USER_SETTABLE_CALL_CENTER_AGENT_STATUSES,
    statusInfo(): CallCenterAgentStatusInfo {
      return getCallCenterAgentStatusInfo(this.agent?.status);
    },
    tone(): string {
      return getCallCenterAgentStatusTone(this.agent?.status);
    },
    isPaused(): boolean {
      return this.agent?.status === 'pause';
    },
    isImpersonating(): boolean {
      return !!this.$store.state.originUserId;
    },
  },
  methods: {
    async init() {
      this.stopPolling();
      this.agent = null;
      if (!this.$store.state.loginUserId || this.$$user.isPartner || !await this.hasSipId()) {
        return;
      }
      await this.loadAgent();
      if (this.agent) {
        this.startPolling();
      }
    },
    /**
     * Prüft die SIP-IDs am eigenen Login-User, sofern sie gelesen werden dürfen.
     * Ohne Leserecht entscheidet das Backend (404 ohne SIP-ID).
     */
    async hasSipId(): Promise<boolean> {
      const loginUserId = this.$store.state.loginUserId as Types.ObjectId;
      const canReadSipIds = this.$$crudAccessManager
          .entity(CrudEntityTypes.LOGIN_USER, loginUserId)
          .canReadAll(LoginUserFields.SIP_IDS);
      if (!canReadSipIds) {
        return true;
      }
      try {
        const loginUser = await UserLoginHandlerApi.findById(loginUserId, {fields: [LoginUserFields.SIP_IDS]});
        return (loginUser?.sipIds || []).some((sipId) => !!sipId?.trim());
      } catch (e) {
        return true;
      }
    },
    async loadAgent() {
      this.lastLoadedAt = Date.now();
      try {
        this.agent = await ApiHandlerApi.placetel.api.getCallCenterAgent();
      } catch (e) {
        // Fehler zeigt bereits der API-Handler an, bisherigen Status beibehalten
        return;
      }
      if (!this.agent) {
        this.stopPolling();
      }
    },
    async refresh() {
      if (!this.agent || this.pendingStatus || document.visibilityState !== 'visible'
          || Date.now() - this.lastLoadedAt < MIN_REFRESH_INTERVAL_MS) {
        return;
      }
      await this.loadAgent();
    },
    async setStatus(status: UserSettableCallCenterAgentStatus) {
      if (!this.agent || this.pendingStatus || this.agent.status === status) {
        return;
      }
      this.pendingStatus = status;
      try {
        // Kein optimistisches Update, die Antwort enthält bereits den neuen Status
        this.agent = await ApiHandlerApi.placetel.api.setCallCenterAgentStatus({status});
        this.lastLoadedAt = Date.now();
      } catch (e) {
        if (isAxiosError(e) && e.response?.status === 502) {
          this.$$showSnackbar('Status konnte nicht geändert werden, bitte erneut versuchen.', 'error', e);
        }
      } finally {
        this.pendingStatus = null;
      }
    },
    startPolling() {
      this.stopPolling();
      this.pollingInterval = window.setInterval(this.refresh, POLLING_INTERVAL_MS);
    },
    stopPolling() {
      if (this.pollingInterval) {
        window.clearInterval(this.pollingInterval);
        this.pollingInterval = undefined;
      }
    },
  },
  watch: {
    visible(value: boolean) {
      if (value) {
        this.refresh();
      }
    },
    '$store.state.loginUserId': {
      immediate: true,
      handler() {
        this.init();
      },
    },
  },
  mounted() {
    window.addEventListener('focus', this.refresh);
  },
  beforeDestroy() {
    this.stopPolling();
    window.removeEventListener('focus', this.refresh);
  },
});
</script>

<style scoped>
/* Pause soll dauerhaft auffallen, ohne nach Stunden zu nerven: ruhiger Ring, der langsam aus dem Punkt läuft */
.pause-pulse ::v-deep .v-badge__badge::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: inherit;
  animation: pause-pulse 2.5s ease-out infinite;
}

@keyframes pause-pulse {
  0% {
    opacity: 0.9;
    transform: scale(1);
  }
  70%, 100% {
    opacity: 0;
    transform: scale(3.2);
  }
}

@media (prefers-reduced-motion: reduce) {
  .pause-pulse ::v-deep .v-badge__badge::after {
    animation: none;
  }
}
</style>

<style src="@/components/common/modern/modern.css"></style>
